import {
  DatePicker,
  Divider,
  Form,
  FormProps,
  Input,
  Modal,
  ModalProps,
  Select,
  useSelect,
} from '@pankod/refine-antd'
import React from 'react'
import locale from 'antd/lib/locale/id_ID'
import dayjs from 'dayjs'
import { useGetIdentity } from '@pankod/refine-core'

import { TLMSEntity } from '@resources/lms-management/entity/types'
import { TCommonError } from 'src/interfaces/common'
import { PROGRAM_LOCAL_STATE, TLMSProgramLocalRequest } from '../types'
import { useProgramStore } from '@resources/lms-management/program/store'
import {
  SelectCityOptions,
  SelectDistrictOptions,
  SelectProvinceOptions,
} from '@resources/basic-ppg-management/lptk/blocks/SelectLPTKDataOptions'

type TModalFormProps = {
  modalProps: ModalProps
  formProps: FormProps
  mode: 'create' | 'edit'
  programName?: string
  onClose: () => void
  onSubmit: () => Promise<void>
}
export const ModalForm = ({
  modalProps,
  formProps,
  mode,
  programName,
  onClose,
  onSubmit,
}: TModalFormProps) => {
  const { data } = useGetIdentity()
  const LMSRole = data?.user?.LMSRole || ''
  const { hasEntity, periode, isOwner } = useProgramStore(
    ({ hasEntity, periode, isOwner }) => ({
      hasEntity,
      periode,
      isOwner,
    }),
  )
  const { selectProps: entitySelectProps } = useSelect<
    TLMSEntity,
    TCommonError
  >({
    resource: 'entities',
    dataProviderName: 'lms',
    optionLabel: 'name',
    optionValue: 'id',
    queryOptions: { enabled: modalProps.visible && !hasEntity },
    defaultValueQueryOptions: {
      enabled: false,
    },
    filters: [
      {
        field: 'pageSize',
        operator: 'eq',
        value: -1,
      },
    ],
  })
  const provinsiId = Form.useWatch('provinsiId', formProps.form)
  const kotaKabId = Form.useWatch('kotaKabId', formProps.form)
  const isAdminEntitas = LMSRole === 'admin-entitas' && !isOwner
  const isProgramLocalPublished =
    formProps?.initialValues?.data?.status === PROGRAM_LOCAL_STATE.PUBLISHED

  return (
    <Modal
      {...modalProps}
      onCancel={onClose}
      title={
        mode === 'create' ? 'Buat Program Lokal Baru' : 'Ubah Program Lokal'
      }
      okText={mode === 'create' ? 'Buat Program Lokal' : 'Simpan'}
      cancelText="Batal"
      okButtonProps={{
        ...modalProps.okButtonProps,
        onClick: async () => {
          const data = await onSubmit()
          formProps.onFinish?.(data)
        },
      }}
    >
      <Form
        {...formProps}
        layout="vertical"
        validateMessages={{ required: '${label} harus diisi.' }}
        initialValues={(() => {
          const { entity, startDate, endDate, ...rest } =
            formProps?.initialValues?.data || {}
          const periode =
            startDate && endDate
              ? [dayjs(startDate), dayjs(endDate)]
              : undefined
          return {
            ...rest,
            periode,
            entityId: entity?.id,
          }
        })()}
      >
        {!hasEntity && (
          <Form.Item
            label="Entitas"
            name="entityId"
            required
            rules={[{ required: true }]}
            getValueFromEvent={(v) => {
              const name = formProps.form?.getFieldValue('name')
              if (!name || name === '') {
                const entityName = entitySelectProps.options?.find(
                  ({ value }) => value === v,
                )?.label
                formProps.form?.setFieldsValue({
                  name: `${entityName}_${programName?.toUpperCase()}`
                    .toLocaleUpperCase()
                    .replaceAll(' ', '_'),
                })
              }
              return v
            }}
          >
            <Select
              {...entitySelectProps}
              disabled={isAdminEntitas || isProgramLocalPublished}
              allowClear
              onSearch={() => {}}
              filterOption={(input, option) =>
                (option!.label as unknown as string)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        )}
        <Form.Item<TLMSProgramLocalRequest>
          label="Nama"
          name="name"
          required
          rules={[{ required: true }]}
        >
          <Input
            autoComplete="off"
            disabled={isAdminEntitas || isProgramLocalPublished}
          />
        </Form.Item>
        <Form.Item label="Periode" name="periode">
          <DatePicker.RangePicker
            format="DD MMM YYYY"
            locale={locale.DatePicker}
            disabledDate={(currentDate) => {
              const [startDate, endDate] = periode
              return (
                currentDate.isBefore(dayjs(startDate).startOf('day')) ||
                currentDate.isAfter(dayjs(endDate).endOf('day'))
              )
            }}
          />
        </Form.Item>
        {mode === 'edit' && (
          <Form.Item<TLMSProgramLocalRequest> name="status" hidden>
            <Input />
          </Form.Item>
        )}
        <Divider dashed orientation="left">
          Wilayah Program Lokal
        </Divider>
        <Form.Item<TLMSProgramLocalRequest>
          label="Provinsi"
          name="provinsiId"
          getValueFromEvent={(data) => {
            formProps?.form?.resetFields(['kotaKabId'])
            formProps?.form?.resetFields(['kecamatanId'])
            return data
          }}
        >
          <SelectProvinceOptions
            autoComplete="off"
            aria-autocomplete="none"
            showSearch
            visible={modalProps.visible}
            allowClear
            disabled={isAdminEntitas || isProgramLocalPublished}
          />
        </Form.Item>
        <Form.Item<TLMSProgramLocalRequest>
          label="Kota/Kabupaten"
          name="kotaKabId"
        >
          <SelectCityOptions
            autoComplete="off"
            aria-autocomplete="none"
            showSearch
            visible={modalProps.visible}
            provinceId={provinsiId}
            disabled={!provinsiId || isAdminEntitas || isProgramLocalPublished}
            allowClear
            initialValue={
              formProps?.initialValues
                ? {
                    city: {
                      name: formProps.initialValues.kotaKab,
                      id: formProps.initialValues.kotaKabId,
                    },
                  }
                : undefined
            }
          />
        </Form.Item>
        <Form.Item<TLMSProgramLocalRequest>
          label="Kecamatan"
          name="kecamatanId"
        >
          <SelectDistrictOptions
            autoComplete="off"
            aria-autocomplete="none"
            showSearch
            visible={modalProps.visible}
            kotaKabId={kotaKabId}
            disabled={!kotaKabId || isAdminEntitas || isProgramLocalPublished}
            allowClear
            initialValue={
              formProps?.initialValues
                ? {
                    district: {
                      name: formProps.initialValues.kecamatan,
                      id: formProps.initialValues.kecamatanId,
                    },
                  }
                : undefined
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
